import { useMemo, useState } from 'react';
import { useLeads } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { useDashboardStats } from '@/hooks/useActionEvents';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { 
  Target, 
  Users, 
  MessageCircle, 
  FileText, 
  Trophy, 
  TrendingDown,
  Info,
  ArrowRight,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/utils';

interface ConversionPerformanceProps {
  isOpen?: boolean;
}

export function ConversionPerformance({ isOpen = true }: ConversionPerformanceProps) {
  const [timeRange, setTimeRange] = useState<7 | 30>(7);
  const { data: leads } = useLeads();
  const { data: deals } = useDeals();
  const { data: stats } = useDashboardStats();

  // Calculate funnel metrics (decision-first: use deal_status for won/lost, exclude closed)
  const funnelData = useMemo(() => {
    if (!leads || !deals) return null;

    // Exclude closed leads (operational decision, not commercial outcome)
    const activeLeads = leads.filter(l => !(l as any).closed_at);
    
    // Get won/lost from deals (commercial outcome), not from leads.pipeline_stage
    const wonDeals = deals.filter(d => (d as any).deal_status === 'won' || d.stage === 'won');
    const lostDeals = deals.filter(d => (d as any).deal_status === 'lost' || d.stage === 'lost');
    const wonLeadIds = new Set(wonDeals.map(d => d.lead_id));
    const lostLeadIds = new Set(lostDeals.map(d => d.lead_id));

    const stages = [
      { id: 'new', label: 'New Leads', count: activeLeads.filter(l => l.pipeline_stage === 'new' && !wonLeadIds.has(l.id) && !lostLeadIds.has(l.id)).length },
      { id: 'contacted', label: 'Contacted', count: activeLeads.filter(l => l.pipeline_stage === 'contacted' && !wonLeadIds.has(l.id) && !lostLeadIds.has(l.id)).length },
      { id: 'appointment', label: 'Qualified', count: activeLeads.filter(l => l.pipeline_stage === 'appointment' && !wonLeadIds.has(l.id) && !lostLeadIds.has(l.id)).length },
      { id: 'proposal', label: 'Proposal', count: activeLeads.filter(l => l.pipeline_stage === 'proposal' && !wonLeadIds.has(l.id) && !lostLeadIds.has(l.id)).length },
      { id: 'won', label: 'Won', count: wonDeals.length }, // Use deals, not leads
    ];

    const total = stages[0].count; // Start from New Leads count
    let previous = total;

    return stages.map((stage, index) => {
      const currentCount = stage.count;
      // Calculate conversion rate from previous stage (not cumulative)
      const conversionFromPrev = index === 0 
        ? 100 
        : (previous > 0 ? (currentCount / previous * 100) : 0);
      const dropOff = index === 0 ? 0 : Math.max(0, (100 - conversionFromPrev)); // Prevent negative
      previous = currentCount; // Use current count for next stage

      return {
        ...stage,
        cumulative: stages.slice(0, index + 1).reduce((sum, s) => sum + s.count, 0),
        percentage: total > 0 ? (currentCount / total * 100) : 0,
        conversionFromPrev: conversionFromPrev > 0 ? conversionFromPrev : 0, // Prevent negative
        dropOff: dropOff,
        isWeakest: false,
      };
    });
  }, [leads, deals]);

  // Find weakest step
  const funnelWithWeakest = useMemo(() => {
    if (!funnelData) return null;
    const weakestIndex = funnelData.slice(1).reduce((maxIdx, step, idx) => 
      step.dropOff > (funnelData[maxIdx + 1]?.dropOff || 0) ? idx : maxIdx
    , 0);
    return funnelData.map((step, idx) => ({
      ...step,
      isWeakest: idx === weakestIndex + 1 && step.dropOff > 20,
    }));
  }, [funnelData]);

  // Campaign KPIs (mock calculations based on available data)
  const campaignKPIs = useMemo(() => {
    if (!leads || !deals) return null;

    const totalLeads = leads.length;
    const wonDeals = deals.filter(d => d.stage === 'won');
    const wonRevenue = wonDeals.reduce((sum, d) => sum + (d.value || 0), 0);
    const leadToClose = totalLeads > 0 ? (wonDeals.length / totalLeads * 100) : 0;

    return [
      { 
        id: 'lead-to-deal', 
        label: 'Lead → Deal', 
        value: `${leadToClose.toFixed(1)}%`,
        tooltip: 'Percentage of leads that became closed-won deals',
        status: leadToClose >= 15 ? 'good' : leadToClose >= 5 ? 'ok' : 'low',
      },
      { 
        id: 'total-leads', 
        label: 'Total Leads', 
        value: totalLeads.toString(),
        tooltip: 'All leads in your database',
        status: 'neutral',
      },
      { 
        id: 'won-deals', 
        label: 'Deals Won', 
        value: wonDeals.length.toString(),
        tooltip: 'Number of closed-won deals',
        status: wonDeals.length > 0 ? 'good' : 'neutral',
      },
      { 
        id: 'avg-deal', 
        label: 'Avg Deal Value', 
        value: wonDeals.length > 0 ? `$${Math.round(wonRevenue / wonDeals.length).toLocaleString()}` : '$0',
        tooltip: 'Average value of won deals',
        status: 'neutral',
      },
    ];
  }, [leads, deals]);

  // Conversion rate over time - calculated from real leads/deals data
  const conversionOverTime = useMemo(() => {
    if (!leads || !deals) return [];
    
    const now = new Date();
    const startDate = subDays(now, timeRange - 1);
    startDate.setHours(0, 0, 0, 0);
    
    // Aggregate leads by day
    const leadsByDay = new Map<string, number>();
    leads.forEach(lead => {
      const leadDate = new Date(lead.created_at);
      leadDate.setHours(0, 0, 0, 0);
      if (leadDate >= startDate) {
        const dateKey = format(leadDate, 'yyyy-MM-dd');
        leadsByDay.set(dateKey, (leadsByDay.get(dateKey) || 0) + 1);
      }
    });
    
    // Aggregate deals by day (deals created, not won)
    const dealsByDay = new Map<string, number>();
    deals.forEach(deal => {
      const dealDate = new Date(deal.created_at);
      dealDate.setHours(0, 0, 0, 0);
      if (dealDate >= startDate && deal.stage !== 'won' && deal.stage !== 'lost') {
        const dateKey = format(dealDate, 'yyyy-MM-dd');
        dealsByDay.set(dateKey, (dealsByDay.get(dateKey) || 0) + 1);
      }
    });
    
    // Build data array for each day in range
    const data = [];
    for (let i = timeRange - 1; i >= 0; i--) {
      const date = subDays(now, i);
      date.setHours(0, 0, 0, 0);
      const dateKey = format(date, 'yyyy-MM-dd');
      const displayDate = format(date, 'MMM d');
      
      const leadsCount = leadsByDay.get(dateKey) || 0;
      const dealsCount = dealsByDay.get(dateKey) || 0;
      const conversionRate = leadsCount > 0 ? (dealsCount / leadsCount) * 100 : 0;
      
      data.push({
        date: displayDate,
        rate: Math.round(conversionRate * 10) / 10, // Round to 1 decimal
      });
    }
    
    return data;
  }, [leads, deals, timeRange]);

  if (!isOpen) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
          <Target className="w-5 h-5 text-success" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Conversion Performance</h2>
          <p className="text-sm text-muted-foreground">Funnel health and deal velocity</p>
        </div>
      </div>

      {/* Conversion Funnel */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-medium">Conversion Funnel</CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[200px] text-xs">
                Shows how leads progress through your sales stages. Red highlights the weakest step.
              </TooltipContent>
            </Tooltip>
          </div>
        </CardHeader>
        <CardContent>
          {funnelWithWeakest ? (
            <div className="space-y-3">
              {funnelWithWeakest.map((step, index) => {
                const Icon = index === 0 ? Users : 
                             index === 1 ? MessageCircle : 
                             index === 2 ? Target : 
                             index === 3 ? FileText : Trophy;
                
                return (
                  <div key={step.id} className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                        step.isWeakest ? "bg-destructive/10" : "bg-muted"
                      )}>
                        <Icon className={cn(
                          "w-4 h-4",
                          step.isWeakest ? "text-destructive" : "text-muted-foreground"
                        )} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <span className={cn(
                            "text-sm font-medium flex-1 min-w-0",
                            step.isWeakest && "text-destructive"
                          )}>
                            {step.label}
                          </span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {index > 0 && step.dropOff > 0 && (
                              <div className={cn(
                                "flex items-center gap-0.5 text-[10px] whitespace-nowrap",
                                step.isWeakest ? "text-destructive font-medium" : "text-muted-foreground"
                              )}>
                                <TrendingDown className="w-2.5 h-2.5" />
                                <span>-{step.dropOff.toFixed(0)}%</span>
                              </div>
                            )}
                            <span className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
                              {step.cumulative} ({step.percentage.toFixed(0)}%)
                            </span>
                          </div>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={cn(
                              "h-full rounded-full transition-all duration-500",
                              step.isWeakest ? "bg-destructive" :
                              index === funnelWithWeakest.length - 1 ? "bg-success" : "bg-primary"
                            )}
                            style={{ width: `${step.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Weakest step callout */}
              {funnelWithWeakest.some(s => s.isWeakest) && (
                <div className="mt-4 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                  <p className="text-xs text-destructive flex items-center gap-2">
                    <TrendingDown className="w-4 h-4" />
                    <span className="font-medium">
                      Biggest drop-off: {funnelWithWeakest.find(s => s.isWeakest)?.label}
                    </span>
                    <ArrowRight className="w-3 h-3" />
                    <span>Focus on improving this stage</span>
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              <p>No funnel data yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Campaign KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {campaignKPIs?.map((kpi) => (
          <Tooltip key={kpi.id}>
            <TooltipTrigger asChild>
              <Card className="kpi-card-clickable group cursor-pointer">
                <CardContent className="pt-4 pb-4">
                  <div className="text-center">
                    <p className={cn(
                      "text-2xl font-bold",
                      kpi.status === 'good' && "text-success",
                      kpi.status === 'low' && "text-warning"
                    )}>
                      {kpi.value}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{kpi.label}</p>
                  </div>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">{kpi.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>

      {/* Conversion Rate Over Time */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-medium">Conversion Rate Over Time</CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[200px] text-xs">
                Estimated conversion rate based on activity trends.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex gap-1">
            <Button
              variant={timeRange === 7 ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setTimeRange(7)}
            >
              7d
            </Button>
            <Button
              variant={timeRange === 30 ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setTimeRange(30)}
            >
              30d
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {conversionOverTime.length > 0 ? (
            <div className="chart-animate-in">
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={conversionOverTime}>
                  <defs>
                    <linearGradient id="colorConversion" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    className="text-muted-foreground"
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    className="text-muted-foreground"
                    tickFormatter={(v) => `${v}%`}
                    width={40}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    formatter={(value: number) => [`${value.toFixed(1)}%`, 'Rate']}
                  />
                  <Area
                    type="monotone"
                    dataKey="rate"
                    stroke="hsl(var(--success))"
                    strokeWidth={2}
                    fill="url(#colorConversion)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-muted-foreground">
              <p>No conversion data yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
